import { Box, FormGroup, Typography } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useRef } from "react";

import { Checkbox, FormControlLabel } from "../";

export interface CheckboxVirtualListOption<T extends string> {
  label: string;
  value: T;
}

export interface CheckboxVirtualListProps<T extends string> {
  options: CheckboxVirtualListOption<T>[];
  value: T[];
  onChange: (value: CheckboxVirtualListProps<T>["value"]) => void;
  height?: number;
  estimateSize?: number;
}

export const CheckboxVirtualList = <T extends string>(
  props: CheckboxVirtualListProps<T>,
) => {
  const { options, value, onChange, height = 300, estimateSize = 38 } = props;

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan: 6,
  });

  const handleChange =
    (optionValue: T) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;

      let newValue: T[];
      if (checked) {
        newValue = [...value, optionValue];
      } else {
        newValue = value.filter((v) => v !== optionValue);
      }

      onChange(newValue);
    };

  return (
    <Box
      ref={parentRef}
      sx={{
        maxHeight: height,
        overflow: "auto",
        position: "relative",
        minHeight: 0,
        px: 3,
      }}
    >
      <FormGroup
        sx={{
          height: rowVirtualizer.getTotalSize(),
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const option = options[virtualRow.index];

          return (
            <Box
              key={option.value}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value.includes(option.value)}
                    onChange={handleChange(option.value)}
                    size="small"
                  />
                }
                sx={{
                  display: "flex",
                }}
                label={<Typography noWrap>{option.label}</Typography>}
              />
            </Box>
          );
        })}
      </FormGroup>
    </Box>
  );
};
